
import { defineComponent, computed, ref, watch, reactive } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { AxiosResponse } from 'axios'
import { ElForm } from 'element-plus'
import { useStore, Store } from '@/store'

import UserLoginWrapper from '@/components/auth-wrappers/UserLoginWrapper.vue'
import InputGroupDark from '@/components/layout/InputGroupDark.vue'
import DoubleScroll from '@/components/layout/DoubleScroll.vue'
import ProximityInfo from '@/components/layout/ProximityInfo.vue'
import InputLabel from '@/components/inputs/InputLabel.vue'
import MessageAttacheFiles from '@/components/inputs/MessageAttacheFiles.vue'
import MessageInfo from '@/components/layout/MessageInfo.vue'
import JrDateMultiPickerButton from '@/components/jr-parts/JrDateMultiPickerButton.vue'
import JrDatePicker from '@/components/jr-parts/JrDatePicker.vue'
import ScheduleStaffListGroup from '@/components/layout/ScheduleStaffListGroup.vue'
import ScheduleSend from '@/components/actions/ScheduleSend.vue'
import JrButton from '@/components/jr-parts/JrButton.vue'
import JrApplicantTitle from '@/components/jr-parts/JrApplicantTitle.vue'
import SchedulePrint from '@/components/actions/SchedulePrint.vue'
import BoundaryInfo from '@/components/layout/BoundaryInfo.vue'
import DateDisplay from '@/components/displays/DateDisplay.vue'
import JrInputView from '@/components/jr-parts/JrInputView.vue'

import { ScheduleSendForm } from '@/types/forms'
import { RequestTypes, ScheduleModes, ScheduleTypes } from '@/constants/enums'
import { RequestHeaderDetail, ScheduleCandidateDateSendModeEnum } from '@/api'
import MessageTypeLogic from '@/services/messageTypeLogic'
import { scheduleMessageRule } from '@/constants/rules'
import { ReadRequest as RequestHeaderReadRequest } from '@/requests/requestHeader'
import Logger from '@/services/logger'
import { addMessageQueue, MESSAGE_TYPE_CONFIRM, MESSAGE_TYPE_ERROR, MESSAGE_TYPE_TOAST } from '@/plugins/messageQueue'
import MessageService from '@/services/message'
import { isAfterToday } from '@/services/date'
import { MessageTypes } from '@/constants/messageTypes'
import { B_WS_DECIDE, B_WS_RESULT, P_WS_DECIDE, P_WS_RESULT } from '@/constants/statuses'
import RequestHeaderLogic from '@/services/requestHeaderLogic'

/**
 * ステータスを取得する
 */
const getStatus = (messageType: number, requestHeader: RequestHeaderDetail): number | null => {
  if (messageType === MessageTypes.P_WS_JR_ADJUST_DATE && requestHeader.proximity != null) {
    // 近接工事現地立会
    if (requestHeader.proximity.witness_status === P_WS_DECIDE) {
      // 日程が確定後
      return STATUSES.DECIDE
    } else if (requestHeader.proximity.witness_status === P_WS_RESULT) {
      // 結果入力後
      return STATUSES.RESULT
    } else {
      // その他送信後
      return STATUSES.SENDED
    }
  } else if (messageType === MessageTypes.B_WS_JR_ADJUST_DATE && requestHeader.boundary != null) {
    // 境界立会現地立会
    if (requestHeader.boundary.witness_status === B_WS_DECIDE) {
      // 日程が確定後
      return STATUSES.DECIDE
    } else if (requestHeader.boundary.witness_status === B_WS_RESULT) {
      // 結果入力後
      return STATUSES.RESULT
    } else {
      // その他送信後
      return STATUSES.SENDED
    }
  }
  return null
}

const STATUSES = Object.freeze({
  FIRST: 1,
  AFTER_TMP_SAVE: 2,
  SENDED: 3, // 送信後
  DECIDE: 4, // 日程確定後
  RESULT: 5, // 結果入力済み
})

/**
 * 共通メッセージ画面
 * JR関係者へ調整を行う画面となる
 */
export default defineComponent({
  name: 'JrScheduleMessageSend',
  components: {
    InputGroupDark,
    DoubleScroll,
    UserLoginWrapper,
    ProximityInfo,
    InputLabel,
    MessageInfo,
    JrDateMultiPickerButton,
    JrDatePicker,
    ScheduleStaffListGroup,
    ScheduleSend,
    JrButton,
    JrApplicantTitle,
    SchedulePrint,
    BoundaryInfo,
    DateDisplay,
    JrInputView,
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const store = useStore() as Store

    const formRef = ref<InstanceType<typeof ElForm>>()
    const attacheFileRef = ref<InstanceType<typeof MessageAttacheFiles>>()

    const scheduleDates = reactive<string[]>([])

    // 動作モード 変更タイミングはメッセージ読み込み・ボタン押下時
    const mode = ref<number | null>(ScheduleModes.NEW)
    // メッセージの状態。変更タイミングはメッセージ等読み込み時と初回の一時保存後
    const status = ref<number | null>(null)

    const form = ref<ScheduleSendForm | null>(null)
    const requestHeader = ref<RequestHeaderDetail | null>(null)
    // 申請情報
    const requestHeaderId = computed(() => {
      const id = Number(route.params.request_header_id)
      return Number.isNaN(id) ? null : id
    })
    // 種別
    const messageType = computed(() => {
      const type = Number(route.params.message_type)
      return Number.isNaN(type) ? null : type
    })
    // 種別名
    const typeName = ref<string>('')
    // 確定種別
    const decideType = ref<number | null>(null)
    // 保守区判定
    const isDepartment = ref<boolean>(false)

    /**
     * 詳細画面に戻る
     */
    const returnDetail = () => {
      if (requestHeader.value == null || requestHeaderId.value == null) {
        return
      }
      if (requestHeader.value.request_type === RequestTypes.PROXIMITY) {
        router.push({
          name: 'ProximityDetail',
          params: {
            request_header_id: requestHeaderId.value,
          },
        })
      } else if (requestHeader.value.request_type === RequestTypes.BOUNDARY) {
        router.push({
          name: 'BoundaryDetail',
          params: {
            request_header_id: requestHeaderId.value,
          },
        })
      }
    }

    /**
     * 送信後のページ遷移
     */
    const sendComplete = (sentResult: boolean) => {
      if (sentResult) {
        addMessageQueue(
          MESSAGE_TYPE_TOAST,
          MessageService.bind(MessageService.codes.SEND_COMPLETE, { target: 'メッセージ' })
        )
        // 送信成功時、詳細画面に遷移させる
        returnDetail()
      }
    }
    /**
     * 保存後
     */
    const saveComplete = (sentResult: boolean) => {
      if (sentResult) {
        if (status.value === STATUSES.FIRST) {
          // 初回の場合、一時保存に変更する
          status.value === STATUSES.AFTER_TMP_SAVE
        }
        addMessageQueue(
          MESSAGE_TYPE_TOAST,
          MessageService.bind(MessageService.codes.SAVE_COMPLETE, { target: 'メッセージ' })
        )
        // 保存成功時、詳細画面に遷移させる
        returnDetail()
      }
    }

    // 申請基本・メッセージ種別変更時は情報取得・ステータス・モード判定を行う
    watch(
      () => [requestHeaderId, messageType],
      async () => {
        mode.value = null
        status.value = null
        decideType.value = null
        Logger.debug('初期読み込み開始')
        if (route.name !== 'JrScheduleMessageSend') {
          // 遷移中(既に他画面となっている時)は何もしない
          return
        }
        // 申請基本IDチェック
        if (requestHeaderId.value == null) {
          Logger.info('申請基本IDが不正です')
          router.push({ name: 'NotFound' })
          return
        }
        // メッセージ種別チェック
        if (messageType.value == null) {
          Logger.info('メッセージ種別が不正です')
          router.push({ name: 'NotFound' })
          return
        }

        const logic = new MessageTypeLogic(messageType.value, requestHeaderId.value)

        if (!logic.isScheduleType() || !logic.isJrReceiveType()) {
          // 日程調整でもなく、JR関係者が受信する物でも無い場合エラー
          Logger.info('メッセージ種別が不正です')
          router.push({ name: 'NotFound' })
          return
        }

        // 申請基本情報の取得
        const request = new RequestHeaderReadRequest(requestHeaderId.value as number)
        await request.exec()

        if (!request.success) {
          return
        }

        const { data } = request.getResponse() as AxiosResponse<RequestHeaderDetail>
        requestHeader.value = data

        // 初期メッセージを取得
        const message = await logic.createConfiguredScheduleMessage()
        form.value = message

        // 種別名を取得
        typeName.value = logic.getTypeName()
        // 確定種別を取得
        decideType.value = logic.getDecideType()

        // 日付の選択肢を合わせる
        scheduleDates.splice(0)
        if (message.schedule_candidate_date != null) {
          scheduleDates.push(
            ...message.schedule_candidate_date.map((candidate) => {
              return candidate.date
            })
          )
        }

        // モード判定
        if (form.value.message_id == null) {
          // メッセージIDが無い場合、新規
          mode.value = ScheduleModes.NEW
          status.value = STATUSES.FIRST
        } else if (form.value.is_sent !== true) {
          // 送信済みではない場合、新規モードで状態は一時保存後
          mode.value = ScheduleModes.NEW
          status.value = STATUSES.AFTER_TMP_SAVE
        } else {
          // メッセージIDが有る場合、参照
          mode.value = ScheduleModes.VIEW
          // 種別・申請基本から状態を判別
          status.value = getStatus(messageType.value, requestHeader.value)
        }
      },
      { immediate: true, deep: true }
    )

    // 候補日が変更された時の動作
    const updateScheduleDates = (dates: string[]) => {
      scheduleDates.splice(0)
      scheduleDates.push(...dates)

      if (form.value != null && form.value.schedule_candidate_date != null) {
        form.value.schedule_candidate_date.splice(0)
        const candidates = dates.map((date) => {
          return {
            date: date,
            selectable_time_type: ScheduleTypes.ALL,
          }
        })

        form.value.schedule_candidate_date.push(...candidates)
      }
    }

    // モード変更時
    watch(
      () => [mode, requestHeader],
      () => {
        Logger.debug(`表示モード: ${mode.value}`)
        if (mode.value == null || requestHeader.value == null) {
          return
        }
        //担当保守区判定ロジック
        const requestHeaderLogic = new RequestHeaderLogic(requestHeader.value)
        if (
          store.getters['user/getUser'] == null ||
          !requestHeaderLogic.isScheduleAdjustUser(store.getters['user/getUser'])
        ) {
          isDepartment.value = false
        } else {
          isDepartment.value = true
        }

        if (mode.value === ScheduleModes.NEW) {
          Logger.debug('新規モード')
          if (!isDepartment.value) {
            Logger.info('新規モードの権限がありません。')
            form.value = null
            // 参照不可な場合、エラーメッセージを出す
            addMessageQueue(
              MESSAGE_TYPE_ERROR,
              MessageService.bind(MessageService.codes.ACTION_FORBIDDEN, { action: '日程調整' }),
              () => returnDetail()
            )
          }
        } else if (mode.value === ScheduleModes.READJUSTMENT || mode.value === ScheduleModes.CHANGE) {
          Logger.debug('再調整')
          if (form.value?.schedule_candidate_date != null) {
            // 明日以降の日付のみ抽出
            const candidates = form.value.schedule_candidate_date
              .filter((candidate) => {
                return isAfterToday(new Date(candidate.date))
              })
              .map((candidate) => candidate.date)
            // 候補日を更新
            updateScheduleDates(candidates)
          }
        }
      },
      { immediate: true, deep: true }
    )

    // ボタン表示
    // 変更を破棄
    const isModeResetVisible = computed(() => {
      // 表示モード以外は出す
      return mode.value !== null && mode.value !== ScheduleModes.VIEW
    })
    // 修正モードボタン
    const isModeFixVisible = computed(() => {
      // 表示モードかつ送信後は出す
      return (
        isDepartment.value &&
        mode.value === ScheduleModes.VIEW &&
        (status.value === STATUSES.SENDED || status.value === STATUSES.DECIDE)
      )
    })
    // 再調整ボタン
    const isModeReadjustmentVisible = computed(() => {
      // 表示モードかつ送信後または日程確定後
      return (
        isDepartment.value &&
        mode.value === ScheduleModes.VIEW &&
        (status.value === STATUSES.SENDED || status.value === STATUSES.DECIDE || status.value === STATUSES.RESULT)
      )
    })
    // 日程・送信先変更ボタン
    const isModeFixScheduleAddressVisible = computed(() => {
      // 表示モードかつ調整中
      return isDepartment.value && mode.value === ScheduleModes.VIEW && status.value === STATUSES.SENDED
    })
    // 決定内容変更ボタン
    const isModeScheduleDecideVisible = computed(() => {
      // 決定後のみ
      return isDepartment.value && mode.value === ScheduleModes.VIEW && status.value === STATUSES.DECIDE
    })
    // 出力ボタン
    const isModeSchedulePrintVisible = computed(() => {
      // 送信後
      return status.value === STATUSES.SENDED || status.value === STATUSES.DECIDE || status.value === STATUSES.RESULT
    })

    const isEdit = computed(() => {
      // 表示モード以外は編集
      return mode.value !== null && mode.value !== ScheduleModes.VIEW
    })
    const isAddressEdit = computed(() => {
      // 再調整モード、日程送信先変更モードは編集
      return (
        mode.value === ScheduleModes.NEW ||
        mode.value === ScheduleModes.READJUSTMENT ||
        mode.value === ScheduleModes.CHANGE
      )
    })
    // 日程調整の編集フラグ
    const isScheduleEdit = computed(() => {
      // 新規・再調整・日程送信先変更のみ日付を触れる
      return (
        mode.value === ScheduleModes.NEW ||
        mode.value === ScheduleModes.READJUSTMENT ||
        mode.value === ScheduleModes.CHANGE
      )
    })

    /**
     * 変更を破棄する
     */
    const destroy = () => {
      addMessageQueue(MESSAGE_TYPE_CONFIRM, MessageService.codes.CONFIRM_DESTROY, () => {
        if (requestHeaderId.value != null && messageType.value != null) {
          // reload
          location.reload()
        }
      })
    }

    return {
      destroy,
      form,
      RequestTypes,
      requestHeader,
      requestHeaderId,
      typeName,
      attacheFileRef,
      formRef,
      rules: scheduleMessageRule,
      scheduleDates,
      updateScheduleDates,
      sendComplete,
      saveComplete,
      mode,
      ScheduleModes,
      status,
      isAfterToday,
      isModeResetVisible,
      isModeFixVisible,
      isModeReadjustmentVisible,
      isModeFixScheduleAddressVisible,
      isModeScheduleDecideVisible,
      isModeSchedulePrintVisible,
      ModeEnum: ScheduleCandidateDateSendModeEnum,
      Statuses: STATUSES,
      isEdit,
      isScheduleEdit,
      isAddressEdit,
      decideType,
      DateDisplay,
    }
  },
})
